// Enhanced Lanes page with Dashboard Master API integration and permission-based UI control
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { StatusBadge, LoadingSpinner, Button } from '../components/Common';
import { ChangeLaneAssignmentModal } from '../components/admin/ChangeLaneAssignmentModal';
import { useAuthContext as useAuth } from '../contexts/AuthContext';
import { canView, canUpdate, getAccessLevel, permissionChecks } from '../lib/auth/helpers';
import { PermissionGuard } from '../lib/auth/helpers';
import { 
  useCheckpointData,
  useLaneGridData,
  useDashboardLoading,
  useDashboardError,
  useDashboardActions
} from '../store/dashboardMasterStore';
import { useApi } from '../hooks/useApi';
import { useDashboardMasterAuth } from '../hooks/useDashboardMasterAuth';
import { getRelativeTime, formatHawaiiDateTime } from '../utils/helpers';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  ArrowPathIcon,
  CubeIcon,
  CubeTransparentIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';

export const LanesPage: React.FC = () => {
  const { checkpointId } = useParams<{ checkpointId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth(); // Use AuthContext instead of AuthStore
  const api = useApi(); // Authenticated API service
  useDashboardMasterAuth(); // Initialize dashboard service with auth tokens
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [refreshing, setRefreshing] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [selectedLane, setSelectedLane] = useState<any>(null);
  
  const checkpointIdNum = checkpointId ? parseInt(checkpointId, 10) : 0;
  
  // Get checkpoint-specific data
  const checkpointData = useCheckpointData(checkpointIdNum);
  const allLaneData = useLaneGridData();
  const isLoading = useDashboardLoading();
  const error = useDashboardError();
  const { fetchDashboardData, forceRefresh } = useDashboardActions();

  // Check permissions for checkpoint features using new system
  
  // Check new queue permissions
  const canViewQueuePage = permissionChecks.canViewQueuePage();
  const canUpdateQueueStatus = permissionChecks.canUpdateQueueStatus();
  const canUpdateQueueAssignment = permissionChecks.canUpdateQueueAssignment();

  useEffect(() => {
    // Fetch data if not available
    if (!checkpointData && !isLoading) {
      fetchDashboardData();
    }
  }, [checkpointData, isLoading]); // Remove function dependency to prevent infinite loop

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // If no access to queue page, show access restricted message
  if (!canViewQueuePage) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Access Restricted</h1>
            <p className="text-gray-400">You don't have permission to view queue information.</p>
          </div>
        </div>
      </div>
    );
  }

  // Filter lane data for this checkpoint
  const checkpointLaneData = allLaneData.filter(lane => lane.checkpointId === checkpointIdNum);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await forceRefresh();
      toast.success('Lane data refreshed');
    } catch (err) {
      toast.error('Failed to refresh lane data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleToggleLaneStatus = async (laneAssignmentId: number, currentStatus: boolean) => {
    try {
      await api.toggleLaneAssignment(
        laneAssignmentId,
        true, // cascade=true as requested
        user,
        `Lanes page toggle: ${currentStatus ? 'closed' : 'opened'} lane assignment`
      );
      
      toast.success(`Lane ${currentStatus ? 'closed' : 'opened'} successfully`);
      
      // Force refresh dashboard data to get updated state
      await forceRefresh();
      
    } catch (err) {
      toast.error('Failed to toggle lane status');
    }
  };

  const handleChangeLaneAssignment = (lane: any) => {
    setSelectedLane(lane);
    setShowChangeModal(true);
  };

  const handleChangeSuccess = async () => {
    // Refresh the dashboard data to show updated lane information
    await forceRefresh();
    toast.success('Dashboard updated with new lane assignment');
  };
  if (isLoading && !checkpointData) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Error Loading Lanes</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Button onClick={() => navigate('/dashboard')} variant="primary">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!checkpointData || !checkpointData.checkpoint || !checkpointData.terminal) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-white mb-4">Checkpoint Not Found</h2>
            <p className="text-gray-400 mb-6">
              The requested checkpoint could not be found.
            </p>
            <Button onClick={handleBack} variant="primary">
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { checkpoint, terminal, computedStats } = checkpointData;

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Button
              onClick={handleBack}
              variant="secondary"
              size="sm"
              className="mr-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back
            </Button>
            <nav className="text-sm text-gray-400">
              <span>Dashboard</span>
              <span className="mx-2">/</span>
              <span className="text-white">{checkpoint.name}</span>
            </nav>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">{checkpoint.name}</h1>
              <p className="mt-2 text-gray-400">
                {terminal.name} • {checkpoint.description}
              </p>
              <div className="flex items-center space-x-4 mt-2">
                <StatusBadge 
                  status={checkpoint.isOpen ? 'open' : 'closed'} 
                  size="lg" 
                />
                {checkpoint.isException && (
                  <div className="flex items-center text-orange-400">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-1" />
                    <span className="text-sm">Exception Mode</span>
                  </div>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Updated {formatHawaiiDateTime(checkpoint.last_updated)} by {checkpoint.updated_by}
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <Button
                onClick={handleRefresh}
                variant="secondary"
                size="sm"
                disabled={refreshing || isLoading}
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center">
              <CubeIcon className="h-6 w-6 text-blue-400" />
              <div className="ml-3">
                <p className="text-sm text-gray-400">Sections</p>
                <p className="text-lg font-bold text-white">{computedStats.sectionsCount}</p>
                <p className="text-xs text-green-400">{computedStats.openSections} open</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center">
              <CubeTransparentIcon className="h-6 w-6 text-purple-400" />
              <div className="ml-3">
                <p className="text-sm text-gray-400">Assets</p>
                <p className="text-lg font-bold text-white">{computedStats.assetsCount}</p>
                <p className="text-xs text-green-400">{computedStats.openAssets} active</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center">
              <ClockIcon className="h-6 w-6 text-orange-400" />
              <div className="ml-3">
                <p className="text-sm text-gray-400">Queue</p>
                <p className="text-lg font-bold text-white">{computedStats.laneAssignmentsCount}</p>
                <p className="text-xs text-green-400">{computedStats.openLaneAssignments} open</p>
              </div>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">
            Queue Assignments ({checkpointLaneData.length})
          </h2>
          {/* <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'grid'
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border-l-0 border ${
                viewMode === 'list'
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
              }`}
            >
              List
            </button>
          </div> */}
        </div>

        {/* Lanes Grid/List */}
        {checkpointLaneData.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-lg">
            <ClockIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Lane Assignments</h3>
            <p className="text-gray-400">
              No lane assignments have been configured for this checkpoint.
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {checkpointLaneData.map((lane) => (
              <div
                key={lane.laneAssignmentId}
                className="bg-gray-800 rounded-lg p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {lane.laneTypeName}
                    </h3>
                    <p className="text-sm text-gray-400 mb-3">
                      {lane.laneTypeDescription}
                    </p>
                    <div className="flex items-center space-x-2 mb-4">
                      <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                        {lane.laneTypePurpose}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                  <div>
                    <p className="text-gray-400">Asset Groups</p>
                    <p className="text-white font-medium">{lane.assetGroupsCount}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Assets</p>
                    <p className="text-white font-medium">{lane.openAssets}/{lane.totalAssets}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Status</p>
                    <p className={`font-medium ${lane.isOpen ? 'text-green-400' : 'text-red-400'}`}>
                      {lane.isOpen ? 'Open' : 'Closed'}
                    </p>
                  </div>
                </div>

                <div className="text-xs text-gray-500 mb-4">
                  Updated {formatHawaiiDateTime(lane.last_updated)} by {lane.updated_by}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Controls</span>
                  <div className="flex items-center space-x-2">
                    {canUpdateQueueStatus && (
                      <button
                        onClick={() => handleToggleLaneStatus(lane.laneAssignmentId, lane.isOpen)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                          lane.isOpen 
                            ? 'bg-green-600 focus:ring-green-500' 
                            : 'bg-gray-600 focus:ring-gray-500'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            lane.isOpen ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                    
                    {canUpdateQueueAssignment && (
                      <button
                        onClick={() => handleChangeLaneAssignment(lane)}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                        title="Change lane assignment type"
                      >
                        <ArrowsRightLeftIcon className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-medium text-white">Lane Assignment Status</h3>
            </div>
            <div className="divide-y divide-gray-700">
              {checkpointLaneData.map((lane) => (
                <div 
                  key={lane.laneAssignmentId} 
                  className="px-6 py-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <h4 className="text-white font-medium">{lane.laneTypeName}</h4>
                        <span className="text-sm text-gray-400">{lane.laneTypePurpose}</span>
                        <span className={`text-sm font-medium ${lane.isOpen ? 'text-green-400' : 'text-red-400'}`}>
                          {lane.isOpen ? 'Open' : 'Closed'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{lane.laneTypeDescription}</p>
                      <div className="flex items-center space-x-6 mt-2 text-sm text-gray-500">
                        <span>{lane.assetGroupsCount} asset groups</span>
                        <span>{lane.openAssets}/{lane.totalAssets} assets active</span>
                        <span>Updated {formatHawaiiDateTime(lane.last_updated)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {canUpdateQueueStatus && (
                        <button
                          onClick={() => handleToggleLaneStatus(lane.laneAssignmentId, lane.isOpen)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                            lane.isOpen 
                              ? 'bg-green-600 focus:ring-green-500' 
                              : 'bg-gray-600 focus:ring-gray-500'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              lane.isOpen ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      )}
                      
                      {canUpdateQueueAssignment && (
                        <button
                          onClick={() => handleChangeLaneAssignment(lane)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                          title="Change lane assignment type"
                        >
                          <ArrowsRightLeftIcon className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Change Lane Assignment Modal */}
      {selectedLane && (
        <ChangeLaneAssignmentModal
          isOpen={showChangeModal}
          onClose={() => setShowChangeModal(false)}
          lane={selectedLane}
          checkpointId={checkpointIdNum}
          onSuccess={handleChangeSuccess}
        />
      )}
    </div>
  );
};